import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, Smartphone } from 'lucide-react'
import QRCode from 'qrcode'
import { api } from '../api'
import { Button, ErrorText } from './ui'

type PhoneSession = {
  id: string
  status: 'waiting' | 'uploading' | 'done' | 'failed'
  upload_url?: string
  storage_key?: string
  filename?: string
  error?: string
}

export function PhoneImageUpload({ ws, category, onUploaded }: {
  ws: string
  category: 'source' | 'environment'
  onUploaded: (key: string, filename?: string) => void
}) {
  const [session, setSession] = useState<PhoneSession | null>(null)
  const [qr, setQR] = useState('')
  const create = useMutation({
    mutationFn: () => api.post<PhoneSession>(`/api/workspaces/${ws}/phone-uploads`, { category }),
    onSuccess: setSession,
  })
  const status = useQuery({
    queryKey: ['phone-upload', ws, session?.id],
    queryFn: () => api.get<PhoneSession>(`/api/workspaces/${ws}/phone-uploads/${session!.id}`),
    enabled: !!session?.id,
    refetchInterval: q => ['done', 'failed'].includes(q.state.data?.status ?? '') ? false : 1200,
  })

  useEffect(() => {
    if (!session?.upload_url) return
    QRCode.toDataURL(session.upload_url, { width: 220, margin: 1, errorCorrectionLevel: 'M' }).then(setQR)
  }, [session?.upload_url])

  useEffect(() => {
    if (status.data?.status === 'done' && status.data.storage_key) {
      onUploaded(status.data.storage_key, status.data.filename)
      setSession(null)
      setQR('')
    }
  }, [status.data?.status, status.data?.storage_key, status.data?.filename, onUploaded])

  if (!session) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <Button type="button" variant="secondary" className="min-h-[66px] w-full border-dashed" onClick={() => create.mutate()} disabled={create.isPending}>
          <Smartphone /> {create.isPending ? 'Preparing…' : 'Upload from phone'}
        </Button>
        <span className="text-xs text-ink-faint">Scan once; no phone sign-in needed.</span>
        <ErrorText error={create.error} />
      </div>
    )
  }

  const live = status.data ?? session
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-[--radius-control] border border-line bg-surface-2 p-4 sm:col-span-3">
      {qr && <img src={qr} alt="QR code for phone photo upload" className="size-36 rounded bg-white p-2" />}
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm font-medium">Scan with your phone</p>
        <p className="text-xs text-ink-faint">Choose the photo on your phone. This field fills automatically when the upload finishes.</p>
        <p className="flex items-center gap-2 text-sm text-brand">
          {live.status === 'waiting' ? <><Smartphone /> Waiting for phone…</>
            : live.status === 'uploading' ? <><Loader2 className="animate-spin" /> Uploading and processing…</>
              : live.status === 'failed' ? `Upload failed: ${live.error ?? 'try again'}` : 'Photo received'}
        </p>
        <Button type="button" variant="ghost" size="sm" onClick={() => { setSession(null); setQR('') }}>
          {live.status === 'failed' ? 'Try again' : 'Cancel'}
        </Button>
      </div>
    </div>
  )
}
